import React, { memo, useEffect, useMemo, useState, SyntheticEvent, ChangeEvent, KeyboardEvent } from 'react';
import {
  Tile,
  TileText,
  TileGroup,
  TextField,
  InputAdornment,
  Typography,
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Button,
  Alert
} from '@one-thd/sui-atomic-components';
import { ArrowDown, Calendar } from '@one-thd/sui-icons';
import { DateRangeValue, DateRangePreset } from '../types';
import { formatDisplayDate, parseMMDDYYYY, formatMMDDYYYY, maskDateInput } from '../../../utils';
import { CustomRangeDrawer } from './CustomRangeDrawer';

export interface DateRangeFilterProps {
  label: string;
  value: DateRangeValue;
  presets: DateRangePreset[];
  onApply: (range: DateRangeValue) => void;
  /**
   * Show the "Enter a custom range" Start/End date fields and calendar drawer. Defaults to true;
   * ActiveOrdersTable passes false because its API only accepts preset date sentinels (ADR-003).
   */
  allowCustomRange?: boolean;
}

const CUSTOM_PRESET_VALUE = 'custom';

const resolveTriggerSummary = (value: DateRangeValue, presets: DateRangePreset[]): string => {
  if (value.preset === CUSTOM_PRESET_VALUE && value.startDate && value.endDate) {
    return `${formatDisplayDate(value.startDate)} - ${formatDisplayDate(value.endDate)}`;
  }
  const match = presets.find((preset) => preset.value === value.preset);
  return match?.label ?? 'Select a date range';
};

const initialPendingPreset = (value: DateRangeValue): string | null =>
  value.preset && value.preset !== CUSTOM_PRESET_VALUE ? value.preset : null;

const initialCustomText = (value: DateRangeValue, field: 'startDate' | 'endDate'): string =>
  value.preset === CUSTOM_PRESET_VALUE && value[field] ? formatMMDDYYYY(value[field] as string) : '';

type ActiveCalendarField = 'startDate' | 'endDate' | null;

const DateRangeFilterComponent: React.FC<DateRangeFilterProps> = memo(
  ({ label, value, presets, onApply, allowCustomRange = true }: DateRangeFilterProps) => {
    useEffect(() => {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('date-range-filter.ready');
    }, []);

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [pendingPreset, setPendingPreset] = useState<string | null>(() => initialPendingPreset(value));
    const [customStartText, setCustomStartText] = useState(() => initialCustomText(value, 'startDate'));
    const [customEndText, setCustomEndText] = useState(() => initialCustomText(value, 'endDate'));
    const [activeCalendarField, setActiveCalendarField] = useState<ActiveCalendarField>(null);

    const triggerSummary = useMemo(() => resolveTriggerSummary(value, presets), [value, presets]);

    const openDrawer = () => {
      setPendingPreset(initialPendingPreset(value));
      setCustomStartText(initialCustomText(value, 'startDate'));
      setCustomEndText(initialCustomText(value, 'endDate'));
      setIsDrawerOpen(true);
    };

    const handleTileChange = (_event: SyntheticEvent, tileValue: string | null) => {
      setPendingPreset(tileValue);
      setCustomStartText('');
      setCustomEndText('');
    };

    const handleCustomTextChange = (field: 'startDate' | 'endDate', raw: string, previousLength: number) => {
      const masked = maskDateInput(raw, previousLength);
      if (field === 'startDate') {
        setCustomStartText(masked);
      } else {
        setCustomEndText(masked);
      }
      setPendingPreset(null);
    };

    const handleCalendarApply = (isoDate: string) => {
      const formatted = formatMMDDYYYY(isoDate);
      if (activeCalendarField === 'startDate') {
        setCustomStartText(formatted);
      } else if (activeCalendarField === 'endDate') {
        setCustomEndText(formatted);
      }
      setPendingPreset(null);
      setActiveCalendarField(null);
    };

    const customStartDate = parseMMDDYYYY(customStartText);
    const customEndDate = parseMMDDYYYY(customEndText);
    const hasCustomInput = customStartText.length > 0 || customEndText.length > 0;
    const customRangeError =
      hasCustomInput && customStartDate && customEndDate && customStartDate > customEndDate
        ? 'End date must be on or after the start date.'
        : undefined;

    const canViewResults = Boolean(pendingPreset || (customStartDate && customEndDate && !customRangeError));

    const handleViewResults = () => {
      if (pendingPreset) {
        const match = presets.find((preset) => preset.value === pendingPreset);
        if (!match) {
          return;
        }
        onApply({ preset: match.value, startDate: match.startDate, endDate: match.endDate });
        setIsDrawerOpen(false);
        return;
      }
      if (customStartDate && customEndDate && !customRangeError) {
        onApply({ preset: CUSTOM_PRESET_VALUE, startDate: customStartDate, endDate: customEndDate });
        setIsDrawerOpen(false);
      }
    };

    const renderDateField = (fieldLabel: string, field: 'startDate' | 'endDate', text: string) => (
      <TextField
        label={fieldLabel}
        placeholder="MM/DD/YYYY"
        fullWidth
        value={text}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          handleCustomTextChange(field, event.target.value, text.length)
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <span
                className="sui-flex sui-cursor-pointer"
                role="button"
                tabIndex={0}
                aria-label={`Open ${fieldLabel.toLowerCase()} calendar`}
                aria-haspopup="dialog"
                aria-expanded={activeCalendarField === field}
                onClick={() => setActiveCalendarField(field)}
                onKeyDown={(event: KeyboardEvent) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    setActiveCalendarField(field);
                  }
                }}
              >
                <Calendar size="small" />
              </span>
            </InputAdornment>
          )
        }}
      />
    );

    return (
      <>
        <button
          type="button"
          onClick={openDrawer}
          aria-haspopup="dialog"
          aria-expanded={isDrawerOpen}
          className="sui-inline-flex sui-h-9 sui-items-center sui-gap-2 sui-whitespace-nowrap sui-rounded-base sui-border-1 sui-border-solid sui-border-strong sui-px-3 sui-text-sm hover:sui-bg-subtle"
        >
          <Calendar size="small" />
          <span className="sui-font-bold">{label}:</span>
          <span>{triggerSummary}</span>
          <ArrowDown size="small" />
        </button>
        <Drawer
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={label}
        >
          <DrawerHeader title={label} onClose={() => setIsDrawerOpen(false)} />
          <DrawerBody>
            {allowCustomRange && (
              <>
                <Typography weight="bold" className="sui-mb-2">
                  Enter a custom range:
                </Typography>
                <div className="sui-flex sui-flex-col sui-gap-3 sui-mb-4">
                  {renderDateField('Start date', 'startDate', customStartText)}
                  {renderDateField('End date', 'endDate', customEndText)}
                  {customRangeError && <Alert status="error">{customRangeError}</Alert>}
                </div>
              </>
            )}
            <Typography weight="bold" className="sui-mb-2">
              Select a range:
            </Typography>
            <TileGroup value={pendingPreset} onChange={handleTileChange} orientation="vertical">
              {presets.map((preset) => (
                <Tile key={preset.value} value={preset.value} aria-label={preset.label}>
                  <TileText>{preset.label}</TileText>
                </Tile>
              ))}
            </TileGroup>
          </DrawerBody>
          <DrawerFooter>
            <Button variant="primary" fullWidth disabled={!canViewResults} onClick={handleViewResults}>
              View Results
            </Button>
          </DrawerFooter>
        </Drawer>
        {allowCustomRange && (
          <CustomRangeDrawer
            open={activeCalendarField !== null}
            value={activeCalendarField === 'startDate' ? customStartDate : customEndDate}
            onBack={() => setActiveCalendarField(null)}
            onApply={handleCalendarApply}
          />
        )}
      </>
    );
  }
);

DateRangeFilterComponent.displayName = 'DateRangeFilter';

const DateRangeFilter = DateRangeFilterComponent;

export { DateRangeFilter };
